import type { BrandConfig } from '@portals-platform/brand-kit';
import { validateBrandConfig } from '@portals-platform/brand-kit';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Providers } from './Providers.js';
import { router } from './router.js';

/**
 * Bootstrap the Certificate Portal application for a specific brand.
 *
 * Call this once from the brand app's main.tsx after importing the brand's
 * theme.css and the global CSS from @portals-platform/ui.
 *
 * @example
 * // apps/brand-brinks-certificate-portal/src/main.tsx
 * import '@portals-platform/ui/globals.css'
 * import './theme.css'
 * import { bootstrap } from '@portals-platform/app-core-certificate-portal'
 * import { brinksConfig } from './brand.config'
 * bootstrap(brinksConfig)
 */
export function bootstrap(config: BrandConfig): void {
  // Validate at startup so misconfigured brands fail loudly, not silently.
  const validatedConfig = validateBrandConfig(config);

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('No #root element found in index.html');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <Providers config={validatedConfig}>
        <RouterProvider router={router} />
      </Providers>
    </StrictMode>,
  );
}
